// 📁 src/pages/OrdersLeafletPage.jsx
import { useEffect, useState } from "react";
import { FileDown, ClipboardList, Loader2 } from "lucide-react";

const MONTHS = [1, 2, 3, 4, 5, 6];

function formatCzDate(input) {
  try {
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("cs-CZ");
  } catch {
    return "—";
  }
}

function endOfDay(input) {
  const d = new Date(input);
  d.setHours(23, 59, 59, 999);
  return d;
}

// 🧾 Volání backendu pro vygenerování PDF objednávky
async function openLeafletOrderPdf({ mesice }) {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  console.log("📦 Token při generování PDF:", token);

  const res = await fetch("/api/orders/from-letak/generate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ mesice }),
  });

  if (!res.ok) {
    let msg = `Chyba ${res.status}`;
    try {
      const err = await res.json();
      if (err?.error) msg = err.error;
    } catch {}
    throw new Error(msg);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) alert("Povol prosím vyskakovací okna pro tuto stránku.");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default function OrdersLeafletPage() {
  const [mesice, setMesice] = useState(2);
  const [loading, setLoading] = useState(false);
  const [validFrom, setValidFrom] = useState(null);
  const [validTo, setValidTo] = useState(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [overlapTip, setOverlapTip] = useState("");

  // 🧭 Načtení metadat aktuálního letáku
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
        console.log("📘 Token při načítání letáku:", token);

        const res = await fetch("/api/letak/meta", {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.ok && json.error) throw new Error(json.error);

        setValidFrom(json?.validFrom || null);
        setValidTo(json?.validTo || null);
        setRows(json?.leafletCount || 0);
        setError("");

        // Tooltip: pokud jsou aktivní pro objednávky 2+ letáky, ukaž info
        const oa = Array.isArray(json?.orderActiveMetas) ? json.orderActiveMetas : [];
        if (oa.length >= 2) {
          const tip = oa
            .map((m) => `• ${formatCzDate(m.validFrom)} – ${formatCzDate(m.validTo)}`)
            .join("\n");
          setOverlapTip(`Současně aktivní pro objednávky:\n${tip}`);
        } else {
          setOverlapTip("");
        }
        // “Aktuální leták” je pouze podle validFrom–validTo.
        // validTo bereme do konce dne, aby leták platil celý poslední den.
        if (!json?.validFrom || !json?.validTo) {
          setExpired(true);
        } else {
          const today = new Date();
          const validToDate = endOfDay(json.validTo);
          setExpired(today > validToDate);
        }

      } catch (err) {
        console.warn("❌ Nepodařilo se načíst metadata akčního letáku:", err?.message || err);
        setError(err?.message || "Nepodařilo se načíst data letáku.");
      }
    })();
  }, []);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      await openLeafletOrderPdf({ mesice });
    } catch (e) {
      alert(e.message || "Nepodařilo se vygenerovat PDF");
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || expired || !!error;

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto", padding: 24 }}>
      {/* HLAVIČKA */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div
          style={{
            height: 40,
            width: 40,
            display: "grid",
            placeItems: "center",
            borderRadius: 12,
            background: "#2563eb",
            color: "#fff",
          }}
        >
          <ClipboardList size={20} />
        </div>
        <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.2 }}>
          Doobjednání akčního letáku
        </h1>
      </div>

      {/* KARTA */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 16 }}>Parametry</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Návrh objednávky z aktuálního akčního letáku.
          </div>
        </div>

        <div style={{ padding: 20 }}>
          {/* DVOU SLOUPCOVÝ LAYOUT */}
          <div
            style={{
              display: "grid",
              gap: 24,
              gridTemplateColumns: "1fr 360px",
            }}
          >
            {/* LEVÝ SLOUPEC – INFO O LETÁKU */}
            <div>
              <label
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                Informace o akčním letáku

                {!!overlapTip && (
                  <span
                    title={overlapTip}
                    style={{
                      display: "inline-grid",
                      placeItems: "center",
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      background: "#eef2ff",
                      color: "#1d4ed8",
                      fontSize: 12,
                      cursor: "help",
                      border: "1px solid #c7d2fe",
                    }}
                  >
                    i
                  </span>
                )}
              </label>
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  background: "#f9fafb",
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: expired ? "#9ca3af" : "#111827",
                }}
              >
                {error ? (
                  <div style={{ color: "red" }}>❌ {error}</div>
                ) : expired ? (
                  <div style={{ color: "#9ca3af" }}>
                    Aktuálně není platný žádný akční leták.
                  </div>
                ) : validFrom && validTo ? (
                  <>
                    <div>
                      <b>Platnost od:</b> {formatCzDate(validFrom)}
                    </div>
                    <div>
                      <b>Platnost do:</b> {formatCzDate(validTo)}
                    </div>
                    {rows > 0 && (
                      <div>
                        <b>Počet položek:</b> {rows}
                      </div>
                    )}
                  </>
                ) : (
                  <div>Načítám informace o letáku...</div>
                )}
              </div>
            </div>

            {/* PRAVÝ SLOUPEC – Zásoba na + TLAČÍTKO */}
            <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 12 }}>
              <div>
                <label
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    display: "block",
                    marginBottom: 8,
                  }}
                >
                  Zásoba na
                </label>
                <select
                  value={String(mesice)}
                  onChange={(e) => setMesice(Number(e.target.value))}
                  disabled={expired}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    background: expired ? "#f3f4f6" : "#fff",
                    color: expired ? "#9ca3af" : "#111827",
                  }}
                >
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m} {m === 1 ? "měsíc" : m < 5 ? "měsíce" : "měsíců"}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <button
                  onClick={handleGenerate}
                  disabled={isDisabled}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    background: isDisabled ? "#d1d5db" : "#2563eb",
                    color: isDisabled ? "#6b7280" : "#fff",
                    border: "none",
                    borderRadius: 10,
                    padding: "12px 16px",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    boxShadow: "0 1px 1px rgba(0,0,0,.05)",
                  }}
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <FileDown size={16} />
                  )}
                  Vygenerovat PDF
                </button>
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: "#e5e7eb", margin: "22px 0" }} />

          <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.55 }}>
            Tento návrh objednávky vychází ze spotřeby za posledních 6 měsíců
            a aktuálního sortimentu akčního letáku. Cílem je doplnit naskladnit dostatečné množství zboží již se sníženou nákupní cenou před začátkem platnosti akce nebo také doplnit zásobu
            před koncem platnosti akce a zajistit lepší marži pro prodeje mimo akci.
          </div>
        </div>
      </div>
    </div>
  );
}
